import React, { useState } from "react";
import PropTypes from "prop-types";

const PasswordInputFieldRegister = ({
  title,
  placeholder,
  width = "400px",
  value,
  onChange,
  validationMessage = "Password is required.",
  iconColor = "#EF4444",
  iconSize = "16px",
  apiEndpoint,
}) => {
  const [error, setError] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  // Individual validation checks
  const validationChecks = [
    {
      id: "length",
      text: "Must be at least 8 characters",
      isValid: value.length >= 8,
    },
    {
      id: "special",
      text: "Must contain one uppercase letter & a special character",
      isValid: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    },
  ];

  const validatePassword = (password) => {
    if (!password.trim()) return "Password is required.";
    if (password.length < 8)
      return "Password must be at least 8 characters long.";
    if (!/[A-Z]/.test(password))
      return "Password must include at least one uppercase letter.";
    if (!/[a-z]/.test(password))
      return "Password must include at least one lowercase letter.";
    if (!/[0-9]/.test(password))
      return "Password must include at least one number.";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password))
      return "Password must include at least one special character.";
    return "";
  };

  const handleFocus = () => setIsFocused(true);

  const handleBlur = () => {
    setIsFocused(false);
    const validationError = validatePassword(value);
    if (validationError) {
      setError(validationError);
    } else {
      setError("");
      // Submit data to the backend if valid
      if (apiEndpoint) {
        fetch(apiEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: value }),
        })
          .then(() => console.log("Password saved successfully."))
          .catch((err) => console.error("Error saving password:", err));
      }
    }
  };

  return (
    <>
      <style>{`
        /* Reuse existing dd- classes */
        .dd-input-group { display: flex; flex-direction: column; gap: 6px; }
        .dd-section-label {
          font-size: 14px; font-weight: 700; color: #0F172A;
          margin-bottom: 6px; display: block;
        }
        .dd-required { color: #EF4444; margin-left: 2px; }
        .dd-input-wrapper { position: relative; width: 100%; }
        .dd-input {
          width: 100%; box-sizing: border-box; padding: 12px 14px;
          border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 14px;
          color: #1E293B; background: #FFFFFF; outline: none;
          transition: border-color 0.15s;
        }
        .dd-input:focus {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .dd-input.dd-input-error {
          border-color: #EF4444; padding-right: 36px;
        }
        .dd-input.dd-input-error:focus {
          border-color: #EF4444;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
        }
        .dd-error-icon {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          color: #EF4444; display: flex;
        }
        .dd-error-msg {
          display: flex; align-items: center; gap: 4px;
          font-size: 12px; color: #EF4444; margin-top: 4px;
        }
        .dd-password-checklist {
          display: flex; flex-direction: column; gap: 8px;
          margin-top: 8px;
        }
        .dd-checklist-item {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px;
        }
      `}</style>

      <div className="dd-input-group" style={{ maxWidth: width }}>
        <label className="dd-section-label">
          {title} <span className="dd-required">*</span>
        </label>

        <div className="dd-input-wrapper">
          <input
            className={`dd-input ${error ? "dd-input-error" : ""}`}
            type="password"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {error && (
            <span className="dd-error-icon" title={error}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z"
                  stroke="#EF4444"
                  strokeWidth="1.5"
                />
                <path
                  d="M12 8v5M11.995 16h.009"
                  stroke="#EF4444"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
        </div>

        {/* Validation checklist when focused or has value */}
        {(isFocused || value) && (
          <div className="dd-password-checklist">
            {validationChecks.map((check) => (
              <div key={check.id} className="dd-checklist-item">
                {check.isValid ? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#CBD5E1"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                )}
                <span
                  style={{
                    color: check.isValid ? "#0F172A" : "#94A3B8",
                    fontWeight: check.isValid ? 500 : 400,
                  }}
                >
                  {check.text}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Error message */}
        {error && !isFocused && (
          <div className="dd-error-msg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#EF4444"
              strokeWidth="1.5"
            >
              <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" />
              <path
                d="M12 8v5M11.995 16h.009"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {error}
          </div>
        )}
      </div>
    </>
  );
};

PasswordInputFieldRegister.propTypes = {
  title: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  width: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  validationMessage: PropTypes.string,
  iconColor: PropTypes.string,
  iconSize: PropTypes.string,
  apiEndpoint: PropTypes.string,
};

// Using default parameters instead of defaultProps to avoid future React warnings
export default PasswordInputFieldRegister;
